import { Box, Group } from '@mantine/core';
import { openModal } from '@mantine/modals';
import { IconChevronRight } from '@tabler/icons-react';
import Invoice from 'app/Private/Invoices/Invoice';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import { useHistory } from 'react-router';
import ActivityDetail from 'shared/components/ActivityDetail';
import { getTitleKeyForActivity } from 'shared/components/ActivityDetail/ActivityDetail';
import HTML from 'shared/components/HTML/HTML';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { Activity } from 'types/store/activity';
import styleIdentifiers from './activitiesItem.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  item: Activity;
};

export default function ActivitiesItem({ item }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const isCreditNote = item.tracked_type === 'CreditNote';

  const openActivity = () => {
    openModal({
      size: 'auto',
      title: t(getTitleKeyForActivity(item as any)),
      children: <ActivityDetail activity={item} />,
    });
  };

  const detailModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    showDialog({
      id: DialogShowId.INVOICE,
      size: DialogShowSize.LARGE,
      children: <Invoice isCreditNote={isCreditNote} id={item.tracked_id} modal />,
    });
  };

  const detailDebtor = (e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(`/clients/${item.tracked_id}`);
  };

  const detailPaymentPlan = (e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(`/payment-plans/${item.payment_plan_id}`);
  };

  const detailTask = (e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(`/tasks/${item.tracked_id}`);
  };

  const shouldShowPaymentPlan = item.payment_plan_id;
  const shouldShowClient = item.tracked_type === 'Debtor' && !shouldShowPaymentPlan;
  const date = dayjs(item.date).format('L LT');

  return (
    <tr className={styles('ActivitiesItem')} key={item.id} onClick={openActivity}>
      <td onClick={openActivity}>
        <Group wrap="nowrap">
          <Box w={10} h={10} miw={10} bdrs="100%" bg={item.new ? 'red.4' : 'transparent'} />
          {item.author}
        </Group>
      </td>
      <td onClick={openActivity}>{date}</td>
      <td onClick={openActivity} className={styles('description')}>
        <HTML html={item.description} />
      </td>
      <td className={styles('actions')}>
        {(item.tracked_type === 'Invoice' || isCreditNote) && (
          <Group gap={8} onClick={detailModal}>
            {t(
              isCreditNote ? i18nKeys.ACTIVITIES.SEE_CREDIT_NOTE : i18nKeys.ACTIVITIES.SEE_INVOICE,
            )}
            <IconChevronRight size={14} stroke={1.5} />
          </Group>
        )}
        {shouldShowClient && (
          <Group gap={8} onClick={detailDebtor}>
            {t(i18nKeys.ACTIVITIES.SEE_CLIENT)}
            <IconChevronRight size={14} stroke={1.5} />
          </Group>
        )}
        {shouldShowPaymentPlan && (
          <Group gap={8} onClick={detailPaymentPlan}>
            {t(i18nKeys.ACTIVITIES.SEE_PAYMENT_PLAN)}
            <IconChevronRight size={14} stroke={1.5} />
          </Group>
        )}
        {item.tracked_type === 'Task' && (
          <Group gap={8} onClick={detailTask}>
            {t(i18nKeys.ACTIVITIES.SEE_TASK)}
            <IconChevronRight size={14} stroke={1.5} />
          </Group>
        )}
      </td>
    </tr>
  );
}
